import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Inject, NgZone, OnDestroy } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { MultiSelectModule } from 'primeng/multiselect';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { BehaviorSubject, debounceTime, filter, Subject, takeUntil } from 'rxjs';
import { BikeModel, BikeModelFilter, getDefaultBikeModel } from '../../interfaces/bikeModel';
import { Column } from '../../interfaces/table';
import { ConfirmDialogsService } from '../../services/confirm-dialogs.service';
import { NotificationsService } from '../../services/notifications.service';
import { getPathKey } from '../../utilities/util';
import { TranslocoDatePipe, TranslocoCurrencyPipe, TranslocoDecimalPipe, TranslocoPercentPipe } from '@jsverse/transloco-locale';
import { DurationPipe } from '../../pipes/duration.pipe';
import { SliderModule } from 'primeng/slider';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { TariffPlan } from '../../interfaces/tariffPlan.interface';
import { LongPressDirective } from '../../directives/long-press.directive';


type MAIN_I = BikeModel
type FILTER_I = BikeModelFilter

@Component({
  selector: 'app-admin-models-view',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    RippleModule,
    InputTextModule,
    TranslocoDirective,
    DialogModule,
    FloatLabelModule,
    InputNumberModule,
    MenuModule,
    MultiSelectModule,
    SelectModule,
    SliderModule,
    SkeletonModule,
    TooltipModule,
    CheckboxModule,
    LongPressDirective
  ],
  providers: [TranslocoDatePipe, TranslocoCurrencyPipe, TranslocoDecimalPipe, TranslocoPercentPipe, DurationPipe],
  templateUrl: './admin-models-view.component.html',
  styleUrl: './admin-models-view.component.scss'
})
export class AdminModelsViewComponent implements OnDestroy, AfterViewInit {
  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- CONFIGURAZIONE BASE -------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? TRADUZIONI
  TITLE = "bikeModelsManagement"
  SEARCH_PLACEHOLDER = "searchModels"
  ADD_BUTTON_LABEL = "newMasculine"
  NEW_RECORD_TITLE = "newModel"
  EDIT_RECORD_TITLE = "editModelWithCode"
  FILTERS_TITLE = "filterModels"
  DATA_SAVED_MESSAGE = "modelSaved"
  DELETE_CONFIRMATION_MESSAGE = "deleteModelConfirm"

  //? DATI
  KEY_NAME = 'modelId'
  DEFAULT_DATA_FN = getDefaultBikeModel
  GET_DATA_ENDPOINT = "getBikeModels"
  GET_RECORD_ENDPOINT = "getBikeModel"
  GET_FILTERS_ENDPOINT = "getBikeModelsFilters"
  CREATE_RECORD_ENDPOINT = "createBikeModel"
  EDIT_RECORD_ENDPOINT = "editBikeModel"
  DELETE_DATA_ENDPOINT = "deleteBikeModels"

  //! configurare columns, dataFilters e _batchMenuStructure 

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- INIZIALIZZAZIONI ----------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? DATI E TABELLE
  columns: Column[] = [
    { field: 'id', header: 'id', visible: false },
    { field: 'code', header: 'code', visible: true },
    { field: 'description', header: 'description', visible: true },
    { field: 'maxDistance', header: 'maxDistanceUom', headerParams: { uom: "km" }, visible: true, pipe: ["decimal", { minimumFractionDigits: 0, maximumFractionDigits: 1 }] },
    { field: 'maxSpeed', header: 'maxSpeedUom', headerParams: { uom: "km/h" }, visible: true, pipe: ["decimal", { minimumFractionDigits: 0, maximumFractionDigits: 1 }] },
  ]
  data: MAIN_I[] = []

  get dataFilterFields() {
    return this.columns.filter(column => column.visible).map(column => column.field);
  }

  selectedRows: any[] = []

  dataLoading$ = new BehaviorSubject<boolean>(false)
  dataLoadingUI$ = this.dataLoading$.pipe(
    debounceTime(200)
  );

  _dataTrackBy: (index: number, item: any) => any = (index: number, item: any) => {
    return item.KEY_NAME
  };

  //? VIEW
  formDialogVisible: boolean = false;
  filtersDialogVisible: boolean = false;
  formDialogData: MAIN_I = this.DEFAULT_DATA_FN();

  dataFilters: FILTER_I = {
    options: {
      maxDistance: 999,
      maxSpeed: 999,
      minDistance: 0,
      minSpeed: 0
    },
    cache: {
      maxDistance: 999,
      maxSpeed: 999,
      minDistance: 0,
      minSpeed: 0
    },
    values: {
      maxDistance: undefined,
      maxSpeed: undefined
    },
    applied: {}
  };
  get appliedFiltersCount() {
    let cnt = Object.values(this.dataFilters.applied).filter(value => value !== undefined && value !== null).length
    return cnt == 0 ? undefined : String(cnt)
  }
  isMaxDistanceFilterEnabled: boolean = false;
  isMaxSpeedFilterEnabled: boolean = false;

  get isNewRecordDialog() {
    return this.formDialogData[(this.KEY_NAME as keyof MAIN_I)] === ''
  }

  _batchMenuStructure: MenuItem[] = [
    {
      label: 'batchOperations',
      items: [
        {
          label: 'deleteSelected',
          icon: 'pi pi-trash',
          command: () => {
            this.deleteRecords()
          }
        }
      ]
    }
  ]

  batchMenu: MenuItem[] = []

  fileState: {
    mainPicturePreview?: string;
    mainPictureFile?: File;
    picture2Preview?: string;
    picture2File?: File;
    picture3Preview?: string;
    picture3File?: File;
  } = {};
  deletedPictures: string[] = [];

  //? ALTRO
  destroy$ = new Subject<void>();
  tariffPlans: TariffPlan[] = []

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- LIFE CYCLE - EVENTI -------------------------------------------
  //^ --------------------------------------------------------------------------------------

  constructor(
    private zone: NgZone,
    private http: HttpClient,
    private confirmDialogs: ConfirmDialogsService,
    private notifications: NotificationsService,
    private transloco: TranslocoService,
    private translocoDate: TranslocoDatePipe,
    private translocoCurrency: TranslocoCurrencyPipe,
    private translocoDecimal: TranslocoDecimalPipe,
    private translocoPercent: TranslocoPercentPipe,
    private durationPipe: DurationPipe,
    @Inject('ENVIRONMENT') private ENV: any
  ) {
    transloco.events$.pipe(
      takeUntil(this.destroy$),
      filter((event) => event.type === "translationLoadSuccess")
    ).subscribe((e) => {
      this.batchMenu = this.translateObjectValues(this._batchMenuStructure, 'label')
    });
    this.refreshData()
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.transloco.langChanges$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.batchMenu = this.translateObjectValues(this._batchMenuStructure, 'label')
    });
  }

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- ALTRO ---------------------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? DATI E TABELLE
  refreshData() {
    this.dataLoading$.next(true);

    const filters: any = {};
    if (this.isMaxDistanceFilterEnabled && this.dataFilters.values.maxDistance) {
      // Clamp the values to the valid range
      this.dataFilters.values.maxDistance = [
        Math.max(this.dataFilters.values.maxDistance[0], this.dataFilters.options.minDistance),
        Math.min(this.dataFilters.values.maxDistance[1], this.dataFilters.options.maxDistance),
      ];
      filters.maxDistance = this.dataFilters.values.maxDistance;
    }
    if (this.isMaxSpeedFilterEnabled && this.dataFilters.values.maxSpeed) {
      // Clamp the values to the valid range
      this.dataFilters.values.maxSpeed = [
        Math.max(this.dataFilters.values.maxSpeed[0], this.dataFilters.options.minSpeed),
        Math.min(this.dataFilters.values.maxSpeed[1], this.dataFilters.options.maxSpeed),
      ];
      filters.maxSpeed = this.dataFilters.values.maxSpeed;
    }

    this.http.post(this.ENV.apiUrl + this.GET_DATA_ENDPOINT, { filters }).subscribe({
      next: (resp: any) => {
        this.dataFilters.applied = structuredClone(filters);
        this.data = resp.data;
        this.selectedRows = [];
        this.dataLoading$.next(false);
        this.http.post(this.ENV.apiUrl + this.GET_FILTERS_ENDPOINT, {}).subscribe({
          next: (resp: any) => {
            this.dataFilters.options = resp?.data;

            // Initialize filters if not set
            if (!this.dataFilters.values.maxDistance) {
              this.dataFilters.values.maxDistance = [
                this.dataFilters.options.minDistance,
                this.dataFilters.options.maxDistance,
              ];
            }
            if (!this.dataFilters.values.maxSpeed) {
              this.dataFilters.values.maxSpeed = [
                this.dataFilters.options.minSpeed,
                this.dataFilters.options.maxSpeed,
              ];
            }

            // Validate and adjust existing ranges
            if (this.dataFilters.values.maxDistance) {
              this.dataFilters.values.maxDistance[0] = Math.max(
                this.dataFilters.values.maxDistance[0],
                this.dataFilters.options.minDistance
              );
              this.dataFilters.values.maxDistance[1] = Math.min(
                this.dataFilters.values.maxDistance[1],
                this.dataFilters.options.maxDistance
              );
            }

            if (this.dataFilters.values.maxSpeed) {
              this.dataFilters.values.maxSpeed[0] = Math.max(
                this.dataFilters.values.maxSpeed[0],
                this.dataFilters.options.minSpeed
              );
              this.dataFilters.values.maxSpeed[1] = Math.min(
                this.dataFilters.values.maxSpeed[1],
                this.dataFilters.options.maxSpeed
              );
            }
          },
        });

        this.http.post(this.ENV.apiUrl + "getTariffPlans", {}).subscribe({
          next: (resp: any) => {
            this.tariffPlans = resp.data;
          }
        })
      },
      error: () => {
        this.dataLoading$.next(false);
      },
    });
  }

  openFilters() {
    this.filtersDialogVisible = true;
  }

  toggleFilters() {
    this.filtersDialogVisible = !this.filtersDialogVisible;
  }

  hideFilters() {
    this.filtersDialogVisible = false;
  }

  openFormDialog(record?: MAIN_I) {
    if (record) {
      this.dataLoading$.next(true);
      this.http.post(this.ENV.apiUrl + this.GET_RECORD_ENDPOINT, { ...record, excludeRefs: "*" }).subscribe({
        next: (resp: any) => {
          this.selectedRows = [];
          this.formDialogData = resp.data;

          // Initialize fileState with existing image paths
          this.fileState = {
            mainPicturePreview: resp.data.mainPicture ? "assets/bike_models/" + resp.data.mainPicture : undefined,
            picture2Preview: resp.data.picture2 ? "assets/bike_models/" + resp.data.picture2 : undefined,
            picture3Preview: resp.data.picture3 ? "assets/bike_models/" + resp.data.picture3 : undefined,
          };

          delete this.formDialogData.mainPicture
          delete this.formDialogData.picture2
          delete this.formDialogData.picture3

          // Reset deletedPictures
          this.deletedPictures = [];

          this.formDialogVisible = true;
          this.dataLoading$.next(false);
        },
        error: () => {
          this.dataLoading$.next(false);
        },
      });
    } else {
      this.formDialogData = this.DEFAULT_DATA_FN();
      this.fileState = {};
      this.deletedPictures = [];
      this.formDialogVisible = true;
    }
  }

  hideFormDialog(form?: NgForm) {
    this.formDialogVisible = false;
    this.onHideFormDialog(form)
  }

  onHideFormDialog(form?: NgForm) {
    this.formDialogData = this.DEFAULT_DATA_FN();
    this.fileState = {};
    this.deletedPictures = [];
    if (form) {
      form.resetForm(this.formDialogData)
    }
    const fileInputs = document.querySelectorAll('.hidden-input');
    fileInputs.forEach((input: any) => {
      input.value = ''; // Reset file inputs
    });
  }

  saveRecord() {
    this.dataLoading$.next(true);

    const formData = new FormData();
    const dto = this.cleanDtoToSend(this.formDialogData);

    // Append files to FormData if they exist
    if (this.fileState.mainPictureFile) {
      formData.append('mainPicture', this.fileState.mainPictureFile);
    }
    if (this.fileState.picture2File) {
      formData.append('picture2', this.fileState.picture2File);
    }
    if (this.fileState.picture3File) {
      formData.append('picture3', this.fileState.picture3File);
    }

    // Append the DTO to FormData
    for (const key in dto) {
      if (dto[key] !== null && dto[key] !== undefined) {
        formData.append(key, dto[key]);
      }
    }

    // Append deleted images to FormData
    if (this.deletedPictures.length > 0) {
      formData.append('deletedPictures', JSON.stringify(this.deletedPictures));
    }

    formData.delete("tariffPlans")
    formData.append("tariffPlans", JSON.stringify(dto["tariffPlans"]));

    // Send the FormData to the server
    this.http.post(this.ENV.apiUrl + (this.isNewRecordDialog ? this.CREATE_RECORD_ENDPOINT : this.EDIT_RECORD_ENDPOINT), formData).subscribe({
      next: (resp: any) => {
        if (resp.code == 201) {
          this.notifications.showNotification({
            severity: "info",
            title: "noModifications",
            addToHistory: false,
          });
        } else {
          this.refreshData();
          this.notifications.showNotification({
            severity: "success",
            title: "operationComplete",
            detail: this.DATA_SAVED_MESSAGE,
            detailParams: {
              count: 1,
            },
            translateDetailsParams: false,
            addToHistory: false,
          });
        }
        this.hideFormDialog();
        this.dataLoading$.next(false);
      },
      error: (err) => {
        this.dataLoading$.next(false);
      },
    });
  }

  deleteRecord() {
    this.confirmDialogs.showDeleteConfirmAuto({
      endpoint: this.DELETE_DATA_ENDPOINT,
      selection: [this.formDialogData],
      message: this.DELETE_CONFIRMATION_MESSAGE,
      idKey: this.KEY_NAME,
      onAcceptClick: () => {
        this.dataLoading$.next(true)
      },
      successCallback: () => {
        this.refreshData()
        this.hideFormDialog()
      },
      completeCallback: () => {
        this.dataLoading$.next(false)
      }
    })
  }

  deleteRecords() {
    this.confirmDialogs.showDeleteConfirmAuto({
      endpoint: this.DELETE_DATA_ENDPOINT,
      selection: this.selectedRows,
      message: this.DELETE_CONFIRMATION_MESSAGE,
      idKey: this.KEY_NAME,
      onAcceptClick: () => {
        this.dataLoading$.next(true)
      },
      successCallback: () => {
        this.refreshData()
        this.selectedRows = []
      },
      completeCallback: () => {
        this.dataLoading$.next(false)
      }
    })
  }

  onFilterToggle(filterType: 'maxDistance' | 'maxSpeed') {
    if (filterType === 'maxDistance') {
      if (this.isMaxDistanceFilterEnabled) {
        // Enable the filter, set the slider values to the current applied range or default range
        this.dataFilters.values.maxDistance = this.dataFilters.applied.maxDistance || [
          this.dataFilters.options.minDistance,
          this.dataFilters.options.maxDistance,
        ];
      }
    }

    if (filterType === 'maxSpeed') {
      if (this.isMaxSpeedFilterEnabled) {
        // Enable the filter, set the slider values to the current applied range or default range
        this.dataFilters.values.maxSpeed = this.dataFilters.applied.maxSpeed || [
          this.dataFilters.options.minSpeed,
          this.dataFilters.options.maxSpeed,
        ];
      }
    }
  }

  //? ALTRO
  translateObjectValues<T>(obj: T, keyToTranslate: string): T {
    if (Array.isArray(obj)) {
      return obj.map(item => this.translateObjectValues(item, keyToTranslate)) as T;
    }

    if (obj && typeof obj === 'object') {
      const result = { ...obj };
      for (const [key, value] of Object.entries(result)) {
        if (key === keyToTranslate && typeof value === 'string') {
          (result as { [key: string]: any })[key] = this.transloco.translate(value);
        } else if (typeof value === 'object') {
          (result as { [key: string]: any })[key] = this.translateObjectValues(value, keyToTranslate);
        }
      }
      return result as T;
    }
    return obj;
  }

  onFiltersDialogHide() {
    this.dataFilters.values = this.getFilterDefaults();
  }

  getFilterDefaults() {
    return {
      maxDistance: [this.dataFilters.options.minDistance, this.dataFilters.options.maxDistance] as [number, number],
      maxSpeed: [this.dataFilters.options.minSpeed, this.dataFilters.options.maxSpeed] as [number, number],
    };
  }

  resetFilters() {
    this.dataFilters.values = {
      maxDistance: [this.dataFilters.options.minDistance, this.dataFilters.options.maxDistance],
      maxSpeed: [this.dataFilters.options.minSpeed, this.dataFilters.options.maxSpeed],
    };
    this.dataFilters.applied = {};
    this.isMaxSpeedFilterEnabled = false;
    this.isMaxDistanceFilterEnabled = false;
  }

  getCellData(col: Column, data: any) {
    let pipe = col.pipe

    let value = getPathKey(data, col.nestedField ?? col.field, col.defaultValue ?? '<span class="null-chip">null</span>')

    if (pipe) {
      if (!Array.isArray(pipe)) {
        pipe = [pipe, {}]
      }
      let pipeconfig = pipe[1]
      pipeconfig = this.replaceObjectValues(pipeconfig, getPathKey(data, col.nestedField ?? col.field, col.defaultValue), data, value)
      switch (pipe[0]) {
        case "date":
          value = this.translocoDate.transform(value, pipeconfig, pipeconfig.locale);
          break

        case "currency":
          value = this.translocoCurrency.transform(value, pipeconfig.display, pipeconfig.numberFormatOptions, pipeconfig.currencyCode, pipeconfig.locale);
          break

        case "percent":
          value = this.translocoPercent.transform(value, pipeconfig, pipeconfig.locale);
          break

        case 'decimal':
          value = this.translocoDecimal.transform(value, pipeconfig, pipeconfig.locale);
          break

        case 'translate':
          value = this.transloco.translate(String(value), pipeconfig)
          break

        case 'duration':
          value = this.durationPipe.transform(value, pipeconfig);
          break

        case "custom":
          value = pipe[1](value)
          break

        default:
          return value;
      }
    }
    return value;
  }

  cleanDtoToSend(dto: any) {
    let res = structuredClone(dto)
    for (const key in res) {
      if (res[key] === undefined || res[key] === null || res[key] === "") {
        delete res[key]
      }
    }
    return res
  }

  parseWrapperInputs(col: Column, value: [any, any]) {
    let inputs = col.wrapperConfig;
    if (inputs) {
      inputs = structuredClone(inputs);
      const rowData = value[0];
      const currentColumnValue = value[1];
      const currentColumnValueOriginal = getPathKey(rowData, col.nestedField ?? col.field, col.defaultValue ?? '<span class="null-chip">null</span>');
      return this.replaceObjectValues(inputs, currentColumnValue, rowData, currentColumnValueOriginal)
    } else {
      return {};
    }
  }

  replaceObjectValues(obj: { [key: string]: any }, currentColumnValue: any, rowData: any, currentColumnValueOriginal: any) {
    if (typeof obj === "object" && obj !== null) {
      let objectCopy = structuredClone(obj);

      for (let key in objectCopy) {
        if (typeof objectCopy[key] === 'string') {
          // Replace {{columnField}} with the corresponding value from rowData
          objectCopy[key] = objectCopy[key].replace(/{{([\w.]+)}}/g, (match: any, path: any) => {
            if (path === 'value') {
              return currentColumnValue;
            }
            return getPathKey(rowData, path, match);
          });
        } else if (typeof objectCopy[key] === 'object') {
          // iterate thru key-val
          for (let subKey in objectCopy[key]) {
            if (typeof objectCopy[key][subKey] === 'string') {
              // Replace {{columnField}} with the corresponding value from rowData
              objectCopy[key][subKey] = objectCopy[key][subKey].replace(/{{([\w.]+)}}/g, (match: any, path: any) => {
                let res = currentColumnValue
                if (path !== 'value') {
                  res = getPathKey(rowData, path, match);
                }
                if (subKey == "--state-color" && res == "base") {
                  res = "var(--p-surface-100)"
                }
                if (subKey == "--state-color-text") {
                  res = res == "base" ? "var(--p-text-color)" : "var(--p-primary-color-text)"
                }
                return res
              });
            }
          }
        }

        const match = key.match(/^(.+)_if_(.+)_(.+)$/);
        if (match) {
          const baseKey = match[1];
          const compareType = match[2];
          const compareValue = match[3];

          let valueToCompare;
          if (compareType === 'val') {
            valueToCompare = currentColumnValueOriginal;
          } else {
            valueToCompare = getPathKey(rowData, compareType, undefined);
          }

          if (String(valueToCompare) === compareValue) {
            if (typeof objectCopy[key] == "string") {
              objectCopy[baseKey] = objectCopy[key].startsWith("%+t%") ?
                this.transloco.translate(objectCopy[key].replace("%+t%", "")) :
                objectCopy[key].replace("%+t%", "");
            } else {
              objectCopy[baseKey] = objectCopy[key]
            }

          }
          delete objectCopy[key];
        }
      }
      return objectCopy;
    }
    return obj
  }

  onFileSelected(event: any, field: string) {
    const file = event.target.files[0];
    if (file) {
      // Store the file as a binary object
      (this.fileState as any)[`${field}File`] = file;

      // Generate a preview for the UI
      const reader = new FileReader();
      reader.onload = () => {
        (this.fileState as any)[`${field}Preview`] = reader.result as string;
      };
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
      }
      reader.readAsDataURL(file);
    }
  }

  clearImage(field: string) {
    // If there's an existing image (from the backend), add it to deletedPictures
    if ((this.fileState as any)[`${field}Preview`] && !(this.fileState as any)[`${field}File`]) {
      this.deletedPictures.push(field);
    }

    // Clear the preview and file
    (this.fileState as any)[`${field}Preview`] = undefined;
    (this.fileState as any)[`${field}File`] = undefined;
  }
}
